import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
  type RowData,
  type SortingState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { SkeletonRows, EmptyState } from '../primitives/Feedback.js';

/**
 * Column metadata this grid understands. Set on `meta` in the column def.
 *
 * `align: 'right'` and `numeric: true` are separate on purpose: a voucher
 * number is right-aligned but is not a figure, and a stock quantity is a figure
 * that a report may choose to left-align in a summary block.
 */
export interface GridColumnMeta {
  align?: 'left' | 'right' | 'center';
  numeric?: boolean;
  /** Fixed width, e.g. '128px'. Omit to let the column flex. */
  width?: string;
  /** Hidden when printing — action columns, row selectors. */
  noPrint?: boolean;
}

declare module '@tanstack/react-table' {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  interface ColumnMeta<TData extends RowData, TValue> extends GridColumnMeta {}
  /* eslint-enable @typescript-eslint/no-unused-vars */
}

/**
 * Re-exported so a screen can define its columns without taking a direct
 * dependency on TanStack. The design system owns the grid; it should own the
 * grid's vocabulary too, and consumers get the `meta` augmentation above for
 * free rather than having to import the same package at a matching version.
 */
export type { CellContext, ColumnDef, Row, SortingState, Table } from '@tanstack/react-table';

export interface DataGridProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  /** Fixed footer row — closing balance, totals. Rendered outside the scroller. */
  footer?: ReactNode;
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  onRowClick?: (row: T) => void;
  /** Highlight a row: an unmatched bank line, a variance over threshold. */
  rowClassName?: (row: T, index: number) => string | undefined;
  /** Turn on windowing. Worth it past a few hundred rows; below that the
   *  measurement cost outweighs the saving. */
  virtualize?: boolean;
  /** Height of the scroll viewport when virtualized. */
  height?: number;
  stickyHeader?: boolean;
  className?: string;
  'aria-label'?: string;
}

const ALIGN = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
} as const;

export function DataGrid<T>({
  data,
  columns,
  footer,
  loading = false,
  emptyTitle = 'Nothing to show',
  emptyDescription,
  onRowClick,
  rowClassName,
  virtualize = false,
  height = 560,
  stickyHeader = true,
  className,
  'aria-label': ariaLabel,
}: DataGridProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const rows = table.getRowModel().rows;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    // Row height comes from the density token, so compact mode windows more rows.
    estimateSize: () => 40,
    overscan: 12,
    enabled: virtualize,
  });

  /**
   * A refetch keeps the rows it already has.
   *
   * Placeholders are for a grid that has nothing to show. Once it has figures,
   * swapping them for grey bars on every reload — a filter chip, a date change,
   * a register refreshing after a post — throws away the row the user was
   * reading and reflows the page twice for a result that is usually the same
   * height. The stale figures are dimmed and marked `aria-busy` instead, so
   * they are visibly not current while staying in place, and the bar in the
   * chrome says the request is in flight.
   */
  const stale = loading && rows.length > 0;

  if (loading && rows.length === 0) {
    return (
      <div className={className}>
        <SkeletonRows rows={10} cols={columns.length} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={className}>
        <EmptyState title={emptyTitle} description={emptyDescription} />
      </div>
    );
  }

  const header = (
    <thead className={cn(stickyHeader && 'sticky top-0 z-[1]')}>
      {table.getHeaderGroups().map((group) => (
        <tr key={group.id} className="bg-[var(--surface-2)]">
          {group.headers.map((h) => {
            const meta = (h.column.columnDef.meta ?? {}) as GridColumnMeta;
            const sortable = h.column.getCanSort();
            const sorted = h.column.getIsSorted();

            return (
              <th
                key={h.id}
                scope="col"
                data-noprint={meta.noPrint ? '1' : undefined}
                style={meta.width ? { width: meta.width } : undefined}
                className={cn(
                  'border-b border-[var(--line-2)] px-[var(--pad)] py-2',
                  'text-[10px] font-medium tracking-[0.08em] whitespace-nowrap text-[var(--ink-3)] uppercase',
                  ALIGN[meta.align ?? 'left'],
                  sortable && 'cursor-pointer select-none hover:text-[var(--ink-2)]',
                )}
                onClick={sortable ? h.column.getToggleSortingHandler() : undefined}
                aria-sort={
                  sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined
                }
              >
                <span
                  className={cn(
                    'inline-flex items-center gap-1',
                    meta.align === 'right' && 'flex-row-reverse',
                  )}
                >
                  {flexRender(h.column.columnDef.header, h.getContext())}
                  {sorted && (
                    <svg width="8" height="5" viewBox="0 0 8 5" fill="none" aria-hidden="true">
                      <path
                        d={sorted === 'asc' ? 'M1 4l3-3 3 3' : 'M1 1l3 3 3-3'}
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );

  const renderRow = (row: Row<T>, index: number, style?: CSSProperties) => (
    <tr
      key={row.id}
      style={style}
      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
      className={cn(
        'bb-row-h border-b border-[var(--line-2)]',
        'transition-colors duration-100 hover:bg-[var(--surface-2)]',
        onRowClick && 'cursor-pointer',
        rowClassName?.(row.original, index),
      )}
    >
      {row.getVisibleCells().map((cell) => {
        const meta = (cell.column.columnDef.meta ?? {}) as GridColumnMeta;
        return (
          <td
            key={cell.id}
            data-noprint={meta.noPrint ? '1' : undefined}
            className={cn(
              'px-[var(--pad)] text-[12.5px] text-[var(--ink)]',
              ALIGN[meta.align ?? 'left'],
              meta.numeric && 'bb-num tabular-nums',
            )}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </tr>
  );

  /** Dimmed and inert while a refetch is in flight; unchanged otherwise. */
  const staleClass = cn(
    'transition-opacity duration-150',
    stale && 'pointer-events-none opacity-55',
  );

  if (!virtualize) {
    return (
      <div aria-busy={stale || undefined} className={cn('overflow-x-auto', staleClass, className)}>
        <table className="w-full border-collapse" aria-label={ariaLabel}>
          {header}
          <tbody>{rows.map((row, i) => renderRow(row, i))}</tbody>
          {footer && <GridFooterRow colSpan={columns.length}>{footer}</GridFooterRow>}
        </table>
      </div>
    );
  }

  const items = virtualizer.getVirtualItems();
  const paddingTop = items.length > 0 ? (items[0]?.start ?? 0) : 0;
  const paddingBottom =
    items.length > 0 ? virtualizer.getTotalSize() - (items[items.length - 1]?.end ?? 0) : 0;

  return (
    <div aria-busy={stale || undefined} className={cn(staleClass, className)}>
      <div ref={scrollRef} className="overflow-auto" style={{ height }}>
        <table className="w-full border-collapse" aria-label={ariaLabel}>
          {header}
          <tbody>
            {paddingTop > 0 && (
              <tr aria-hidden="true">
                <td colSpan={columns.length} style={{ height: paddingTop }} />
              </tr>
            )}
            {items.map((vi) => {
              const row = rows[vi.index];
              return row ? renderRow(row, vi.index) : null;
            })}
            {paddingBottom > 0 && (
              <tr aria-hidden="true">
                <td colSpan={columns.length} style={{ height: paddingBottom }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {footer && (
        <table className="w-full border-collapse">
          <GridFooterRow colSpan={columns.length}>{footer}</GridFooterRow>
        </table>
      )}
    </div>
  );
}

function GridFooterRow({ children }: { colSpan: number; children: ReactNode }) {
  return <tfoot className="bg-[var(--surface-2)] font-medium">{children}</tfoot>;
}

/**
 * Total row for a grid footer. Kept separate from the body so a virtualized
 * ledger still shows its closing balance without scrolling to the end.
 */
export function TotalRow({
  cells,
  className,
}: {
  cells: Array<{
    content: ReactNode;
    align?: 'left' | 'right' | 'center';
    numeric?: boolean;
    width?: string;
  }>;
  className?: string;
}) {
  return (
    <tr className={cn('bb-row-h border-t-2 border-[var(--line)]', className)}>
      {cells.map((c, i) => (
        <td
          key={i}
          style={c.width ? { width: c.width } : undefined}
          className={cn(
            'px-[var(--pad)] text-[12.5px] font-semibold text-[var(--ink)]',
            ALIGN[c.align ?? 'left'],
            c.numeric && 'bb-num tabular-nums',
          )}
        >
          {c.content}
        </td>
      ))}
    </tr>
  );
}
